import { CloseIconWithCircle } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, fontWeights, HorizontalSeparator, MenuList, MenuListItemType, Paragraph } from '@va/ui/design-system';
import { useTooltipContext } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAvailableAiModels } from '../api';
import { getAiModelIcon } from '../helpers';
import { AiChatbotView } from '../types';
import { useAiChatbotContext } from './aiChatbotContext';

type AiModelsDropdownProps = {
  selectedModel: string | null;
  buttonClassName?: string;
  setSelectedModel?: (model: string) => void;
  disabled?: boolean;
};

export const AiModelsDropdown = ({
  selectedModel,
  buttonClassName,
  setSelectedModel,
  disabled,
}: AiModelsDropdownProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const translate = useTranslate();
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const { data: aiModels, isLoading } = useAvailableAiModels();

  const optionItems = useMemo<MenuListItemType[]>(
    () =>
      aiModels?.flatMap((model) =>
        model.consented
          ? {
              id: model.name,
              label: model.name,
              value: model.name,
              icon: getAiModelIcon(model.name),
              selected: model.name === selectedModel || undefined,
              className: 'min-h-24u',
              labelClassName: 'text-interface-regular! leading-interface-regular!',
              onClick: () => {
                setSelectedModel?.(model.name);
                setDropdownVisible(false);
                buttonRef.current?.focus();
              },
            }
          : [],
      ) ?? [],
    [aiModels, selectedModel, setSelectedModel],
  );

  return (
    <Button
      ref={buttonRef}
      text={selectedModel ?? optionItems[0]?.label ?? translate('aiChatbot.chatInput.selectModel.placeholder')}
      rightSideIcon={<DropdownArrow open={isDropdownVisible} color='#696969' />}
      color='tertiary'
      tooltip={
        optionItems?.length ? <AiModelsDropdownTooltip optionItems={optionItems} /> : <NoModelsDropdownTooltip />
      }
      tooltipProps={{
        trigger: 'click',
        tooltipClassNames: 'rounded-t-6u! rounded-b-10u w-[15rem] shadow-outline',
        arrow: false,
        open: isDropdownVisible,
        onOpenChange: (open) => {
          if (!open) buttonRef.current?.focus();
          setDropdownVisible(open);
        },
        useDefaultStyle: false,
      }}
      className={classNames('py-7u px-6u!', buttonClassName)}
      textClasses='text-interface-regular leading-interface-regular'
      size='small'
      disabled={isLoading || disabled}
    />
  );
};

type AiModelsDropdownTooltipProps = {
  optionItems: MenuListItemType[];
};

const AiModelsDropdownTooltip = ({ optionItems }: AiModelsDropdownTooltipProps) => {
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);
  const { context } = useTooltipContext();

  const closeDropdown = useCallback(() => context.onOpenChange(false), [context]);

  useIsolatedFocusedContainer({ containerRef, enabled: !!context?.open });

  return (
    <div className='bg-white' ref={containerRef}>
      <div className='mb-1u flex items-center justify-between pl-10u'>
        <Paragraph
          weight={fontWeights.medium}
          variant={'interface'}
          variantSize={'regular'}
          colorClassName={'text-neutralBase-85'}
        >
          {translate('aiChatbot.chatInput.selectModel.title')}
        </Paragraph>
        <Button
          size='small'
          color='quinary'
          icon={(_, color) => <CloseIconWithCircle className='w-10u h-10u' color={color} />}
          onClick={closeDropdown}
        />
      </div>
      <HorizontalSeparator className={'-mx-4u'} colorClassName={'bg-neutralBase-85'} />
      <div className='p-4u bg-white '>
        <MenuList className={'my-1u'} items={optionItems} />
        <Button
          className='w-full'
          color='tertiary'
          size='small'
          textClasses='text-interface-regular leading-interface-regular'
          text={translate('button.cancel')}
          onClick={closeDropdown}
        />
      </div>
    </div>
  );
};

export const NoModelsDropdownTooltip = () => {
  const { setSelectedView } = useAiChatbotContext();
  const translate = useTranslate();

  const containerRef = useRef<HTMLDivElement>(null);
  const { context } = useTooltipContext();

  useIsolatedFocusedContainer({ containerRef, enabled: !!context?.open });

  return (
    <div ref={containerRef} className={'bg-white p-4u flex flex-col gap-4u'}>
      <Paragraph variant={'body'} variantSize={'small'}>
        {translate('aiChatbot.chatInput.noModelsAvailable')}
      </Paragraph>
      <Button
        size={'small'}
        color={'tertiary'}
        text={translate('aiChatbot.chatInput.goToAssistantSettings')}
        textClasses={'text-body-small'}
        className={'w-full'}
        onClick={() => {
          setSelectedView(AiChatbotView.Settings);
        }}
      />
    </div>
  );
};
